<template>
  <div id="song-player" class="h-full">
    <transition-fade :duration="500" appear>
      <div
        v-if="routedSong.vocalMix || routedSong.karaokeSoundMix"
        class="karaokePlayer-view flex h-full flex-col"
      >
        <div class="grow-0 shrink-0">
          <span class="title text-[17px] font-light">{{
            routedSong.title
          }}</span>
        </div>

        <karaoke-lyrics
          :cuePoints="routedSong?.cuePoints"
          :currentTime="currentTime"
        />

        <ul
          class="karaokePlayer-toolbox flex justify-between w-full grow-0 shrink-0"
        >
          <li
            class="bg-white bg-opacity-50 rounded-[6px] grow-0 shrink basis-[32%]"
          >
            <div
              class="songMetaDetails items-center bg-white rounded-t-[6px] flex justify-between p-[21px]"
            >
              <span class="title grow text-[17px] font-light">{{
                routedSong.title
              }}</span>
              <span class="duration text-orange-500 text-[11px]">
                {{
                  (routedSong.vocalMix || routedSong.karaokeSoundMix)
                    ?.durationFormatted
                }}
              </span>
            </div>
            <div class="musicDownload">
              <a
                v-if="routedSong.sheetNotes?.publicUrl"
                :href="routedSong.sheetNotes.publicUrl"
                target="_blank"
                class="text-orange-500 flex text-[17px] justify-between p-[21px]"
              >
                <span class="download-label--primary font-medium"
                  >Bladmuziek</span
                >
                <span class="download-label--secondary font-light text-right"
                  >download (pdf)</span
                >
              </a>
            </div>
          </li>

          <li
            v-if="routedSong.vocalMix?.publicUrl"
            class="bg-white bg-opacity-50 rounded-[6px] grow-0 shrink basis-[32%]"
          >
            <div class="musicDownload">
              <a
                :href="routedSong.vocalMix.publicUrl"
                target="_blank"
                class="text-orange-500 flex text-[17px] justify-between p-[21px]"
              >
                <span class="download-label--primary font-medium"
                  >Ingezongen</span
                >
                <span class="download-label--secondary font-light text-right"
                  >download (mp3)</span
                >
              </a>
            </div>
            <div
              class="audioPlayer flex items-center h-[51px] min-w-[200px] pt-[1px] px-[19px] pb-[12px]"
            >
              <song-player
                :song="routedSong.vocalMix"
                v-model:playing="vocalMixPlayerPlaying"
                @update:currentTime="currentTime = $event"
                v-model:volume="volume"
                v-model:muted="muted"
              />
            </div>
          </li>

          <li
            v-if="routedSong.karaokeSoundMix?.publicUrl"
            class="bg-white bg-opacity-50 rounded-[6px] grow-0 shrink basis-[32%]"
          >
            <div class="musicDownload">
              <a
                :href="routedSong.karaokeSoundMix.publicUrl"
                target="_blank"
                class="text-orange-500 flex text-[17px] justify-between p-[21px]"
              >
                <span class="download-label--primary font-medium"
                  >Soundmix</span
                >
                <span class="download-label--secondary font-light text-right"
                  >download (mp3)</span
                >
              </a>
            </div>
            <div
              class="audioPlayer flex items-center h-[51px] min-w-[200px] pt-[1px] px-[19px] pb-[12px]"
            >
              <song-player
                :song="routedSong.karaokeSoundMix"
                v-model:playing="karaokeSoundMixPlayerPlaying"
                @update:currentTime="currentTime = $event"
                v-model:volume="volume"
                v-model:muted="muted"
                class="text-blue-500"
              />
            </div>
          </li>
        </ul>
      </div>
    </transition-fade>
  </div>
</template>

<script>
import axios from "axios";

import { mapGetters } from "vuex";

import KaraokeLyrics from "../../../components/content/media/music/karaoke/KaraokeLyrics.vue";
import SongPlayer from "../../../components/content/media/music/song/SongPlayer.vue";
import TransitionFade from "../../../components/magic/TransitionFade.vue";

export default {
  name: "KaraokePlayer",
  components: {
    KaraokeLyrics,
    SongPlayer,
    TransitionFade,
  },
  data() {
    return {
      vocalMixPlayerPlaying: false,
      vocalMixPlaybackTracked: false,

      karaokeSoundMixPlayerPlaying: false,
      karaokeSoundMixPlaybackTracked: false,

      currentTime: 0,
      muted: false,
      volume: 75,
    };
  },
  computed: {
    ...mapGetters(["routedSong"]),
  },
  watch: {
    routedSong() {
      // Reset the instance data - except muted & volume.
      this.vocalMixPlayerPlaying = false;
      this.vocalMixPlaybackTracked = false;

      this.karaokeSoundMixPlayerPlaying = false;
      this.karaokeSoundMixPlaybackTracked = false;

      this.currentTime = 0;
    },
    vocalMixPlayerPlaying(playing) {
      if (playing) {
        this.karaokeSoundMixPlayerPlaying = false;
      }
      if (
        !this.vocalMixPlaybackTracked &&
        this.routedSong?.vocalMix?.trackingUrl
      ) {
        // Track song playback.
        axios.get(this.routedSong.vocalMix.trackingUrl, {
          withCredentials: true,
        });
        this.vocalMixPlaybackTracked = true;
      }
    },
    karaokeSoundMixPlayerPlaying(playing) {
      if (playing) {
        this.vocalMixPlayerPlaying = false;
      }
      if (
        !this.karaokeSoundMixPlaybackTracked &&
        this.routedSong?.karaokeSoundMix?.trackingUrl
      ) {
        // Track karaoke soundmix playback.
        axios.get(this.routedSong.karaokeSoundMix.trackingUrl, {
          withCredentials: true,
        });
        this.karaokeSoundMixPlaybackTracked = true;
      }
    },
    volume(volume) {
      window.localStorage.setItem("volume", volume);
    },
  },
  mounted() {
    this.applyMemorizedVolume();
  },
  updated() {
    this.applyMemorizedVolume();
  },
  methods: {
    applyMemorizedVolume() {
      this.volume = Number(window.localStorage.getItem("volume") ?? 75);
    },
  },
};
</script>
